// Pure geometry for face normalization. Zero DOM — every function here is
// unit-testable with plain fixtures.
//
// Similarity transform pipeline (same matrix drives the canvas drawImage AND
// the overlay landmark mapping, so mesh and pixels stay registered):
//
//   source px ──(translate −mid)──▶ eye-midpoint origin
//             ──(scale s)─────────▶ target interocular distance
//             ──(rotate −θ)───────▶ eyes level (deroll)
//             ──(translate +C)────▶ eye midpoint at (0.5·W, 0.36·H)
//
import {
  OUT_W,
  OUT_H,
  EYE_MID_X_FRAC,
  EYE_MID_Y_FRAC,
  TARGET_IOD_FRAC,
  MAX_UPSCALE,
  COVER_SCALE_MAX,
  LEFT_EYE_CORNERS,
  RIGHT_EYE_CORNERS,
} from './constants';

const meanPoint = (landmarks, indices, srcW, srcH) => {
  let x = 0;
  let y = 0;
  indices.forEach((i) => {
    x += landmarks[i].x;
    y += landmarks[i].y;
  });
  return { x: (x / indices.length) * srcW, y: (y / indices.length) * srcH };
};

// MediaPipe landmarks are 0..1 fractions of the source image. Returns eye
// centers (mean of the inner/outer corners) in source pixels.
export function eyeCentersPx(landmarks, srcW, srcH) {
  return {
    left: meanPoint(landmarks, LEFT_EYE_CORNERS, srcW, srcH),
    right: meanPoint(landmarks, RIGHT_EYE_CORNERS, srcW, srcH),
  };
}

// Minimal scale at which every corner of the output frame maps back inside
// the source image — i.e. the smallest zoom with NO filler borders. Inverse
// of transformPoint: src = mid + R(+θ)·(out − target)/s, so each output
// corner's rotated offset from the eye target must fit within the distance
// from the eye midpoint to the corresponding source edge.
export function minCoverScale(theta, mid, target, srcW, srcH) {
  const cos = Math.cos(theta);
  const sin = Math.sin(theta);
  const corners = [
    [0, 0],
    [OUT_W, 0],
    [0, OUT_H],
    [OUT_W, OUT_H],
  ];
  let sMin = 0;
  corners.forEach(([cx, cy]) => {
    const dx = cx - target.x;
    const dy = cy - target.y;
    const vx = dx * cos - dy * sin;
    const vy = dx * sin + dy * cos;
    const need = (v, room) => (room > 0 ? v / room : Infinity);
    if (vx > 0) sMin = Math.max(sMin, need(vx, srcW - mid.x));
    if (vx < 0) sMin = Math.max(sMin, need(-vx, mid.x));
    if (vy > 0) sMin = Math.max(sMin, need(vy, srcH - mid.y));
    if (vy < 0) sMin = Math.max(sMin, need(-vy, mid.y));
  });
  return sMin;
}

// left/right eye centers in source px → similarity transform parameters.
// Scale resolution: aim for TARGET_IOD_FRAC (clamped to MAX_UPSCALE), then
// raise to the border-free coverage minimum (capped at COVER_SCALE_MAX) —
// an off-center face crops tighter rather than showing black filler.
export function computeNormalizeTransform(left, right, srcW, srcH) {
  const dx = right.x - left.x;
  const dy = right.y - left.y;
  const iod = Math.hypot(dx, dy);
  if (!iod) return null;
  const theta = Math.atan2(dy, dx);
  const mid = { x: (left.x + right.x) / 2, y: (left.y + right.y) / 2 };
  const target = { x: EYE_MID_X_FRAC * OUT_W, y: EYE_MID_Y_FRAC * OUT_H };
  const sTarget = Math.min((TARGET_IOD_FRAC * OUT_W) / iod, MAX_UPSCALE);
  const sCover = Math.min(
    minCoverScale(theta, mid, target, srcW, srcH),
    COVER_SCALE_MAX
  );
  return { s: Math.max(sTarget, sCover), theta, mid, target };
}

// Source-pixel point → output-pixel point through the similarity transform.
export function transformPoint(p, t) {
  const x = (p.x - t.mid.x) * t.s;
  const y = (p.y - t.mid.y) * t.s;
  const cos = Math.cos(-t.theta);
  const sin = Math.sin(-t.theta);
  return {
    x: x * cos - y * sin + t.target.x,
    y: x * sin + y * cos + t.target.y,
  };
}

// All landmarks → output-space fractions { x, y, z }. x/y may fall outside
// [0,1] for points pushed past the crop edge — the overlay skips those.
// z is MediaPipe's relative depth (smaller = nearer), passed through for
// depth-weighted glow.
export function landmarksToOutputFractions(landmarks, t, srcW, srcH) {
  return landmarks.map((lm) => {
    const p = transformPoint({ x: lm.x * srcW, y: lm.y * srcH }, t);
    return { x: p.x / OUT_W, y: p.y / OUT_H, z: lm.z || 0 };
  });
}

// FACE_LANDMARKS_TESSELATION ({start, end}[]) → [[a, b]] index pairs, taking
// every `stride`-th edge to thin the mesh for rendering.
export function strideEdges(tessellation, stride) {
  const edges = [];
  for (let i = 0; i < tessellation.length; i += stride) {
    edges.push([tessellation[i].start, tessellation[i].end]);
  }
  return edges;
}
